import { Config, Driver, driver } from "driver.js";

import "driver.js/dist/driver.css";
import "./panelDriver.css";

import { waitSomeTime, X_ICON, findElement, awaitMobileMenu } from "./helpers";
import { MIN_READING_TIME } from "./helpers/constants";
import { Notify } from "../components/Notifications";

import { DriveInput, DriverData } from "./types";
import { PureVoidFunc } from "../helpers/functions/types";

const defaultDriverOptions: Config = {
  showProgress: true,
  overlayColor: "rgb(95 95 86)",
  popoverClass: "vviinn-popover",
  allowClose: false,
};

const generateDriverStep = (params: DriveInput, title: string) => {
  const {
    anchorElement,
    description,
    side,
    align,
    onNextClick,
    onPreviousClick,
    customNotification,
  } = params;
  return {
    element: anchorElement,
    popover: {
      title,
      description,
      side: side ?? "left",
      align: align ?? "start",
      customNotification,
      onNextClick: async () => {
        return onNextClick ? await onNextClick() : true;
      },
      onPreviousClick: async () => {
        return onPreviousClick ? await onPreviousClick() : true;
      },
    },
  };
};

const getButtonById = (id: string) => {
  return document.getElementById(id) as HTMLButtonElement;
};

const Subtitle =
  " <p id='popover-subtitle' class='vviinn-popover driver-popover-subtitle'></p>";

const stylePopoverElements = (
  popoverButton: HTMLButtonElement,
  progress: HTMLElement,
  popoverTitle: HTMLElement,
  initText?: string,
) => {
  popoverButton.innerHTML = "";
  popoverButton.insertAdjacentHTML("afterend", X_ICON);
  popoverButton.style.display = "block";

  progress.innerText = progress.innerText.replace("of", " / ");

  popoverTitle.insertAdjacentHTML("afterend", Subtitle);
  const subtitle = document.getElementById("popover-subtitle");
  if (subtitle) subtitle.innerText = initText ?? "";
  const overlay = document.getElementsByClassName(
    "driver-overlay-animated",
  )[0] as HTMLElement;
  if (overlay) overlay.style.zIndex = "500";
};

const toggleStepButton = (buttonId: string, block: boolean) => {
  const stepButton = getButtonById(buttonId);
  if (stepButton) stepButton.disabled = block;
};

const setBgAnimation = () =>
  document
    .getElementsByClassName("driver-overlay-animated")[0]
    .animate([{ opacity: "1" }, { opacity: "0.5" }, { opacity: "1" }], {
      duration: 2000,
      fill: "forwards",
      easing: "ease-in-out",
      iterations: Infinity,
    });

const toggleTourControls = (block: boolean) => {
  toggleStepButton("popoverNextId", block);
  toggleStepButton("popoverPrevId", block);
};

type GuideTriggerData = {
  clickType: "next" | "previous";
  onClickAction: () => boolean | Promise<boolean>;
  notify: Notify;
  targetDriver?: Driver;
  onDestroyed?: PureVoidFunc;
  stepTarget?: string;
  customNotification?: string;
};

const triggerClickStart = async ({
  clickType,
  onClickAction,
  notify,
  targetDriver,
  onDestroyed,
  stepTarget,
  customNotification,
}: GuideTriggerData) => {
  if (!targetDriver) return;
  toggleTourControls(true);
  (
    document.getElementsByClassName("driver-overlay")[0] as HTMLElement
  ).style.zIndex = "20";
  const animation = setBgAnimation();
  const clickStatus = await onClickAction();
  if (!clickStatus) {
    notify(
      "Error",
      customNotification ?? "Slow internet connection, please try again later",
    );
    animation.cancel();
    toggleTourControls(false);
  } else {
    if (targetDriver.isLastStep() && clickType === "next") {
      if (onDestroyed) onDestroyed();
      targetDriver.destroy();
    } else {
      if (animation) animation.cancel();
      await awaitMobileMenu(stepTarget);
      if (clickType === "next") targetDriver.moveNext();
      else targetDriver.movePrevious();
      toggleTourControls(true);
      await waitSomeTime();
      toggleTourControls(true);
      setTimeout(() => {
        toggleTourControls(false);
      }, MIN_READING_TIME);
    }
  }
};

export const generateDriver = (
  customData: DriverData,
  notify: Notify,
  onDestroyed?: PureVoidFunc,
) => {
  const { driverHeader, stepsData, customSkip } = customData;
  const driverSteps = stepsData.map((data) => {
    return generateDriverStep(data, driverHeader);
  });

  const targetDriver = driver({
    ...defaultDriverOptions,
    onPopoverRender: (
      { title, nextButton, closeButton, progress },
      { state },
    ) => {
      stylePopoverElements(
        closeButton,
        progress,
        title,
        stepsData[state.activeIndex ?? 0].stepTitle,
      );
      nextButton.id = "popoverNextId";
      const skipButton = getButtonById("skipTourButton");
      if (skipButton) {
        skipButton.onclick = () => {
          if (customSkip) customSkip();
          targetDriver.destroy();
        };
      }
    },
    steps: driverSteps.map(({ element, popover }, index) => {
      return {
        element,
        popover: {
          ...popover,
          onNextClick: async () => {
            await triggerClickStart({
              clickType: "next",
              onClickAction: popover.onNextClick,
              notify,
              targetDriver,
              onDestroyed,
              stepTarget: driverSteps[index + 1]?.element,
              customNotification: popover.customNotification,
            });
          },
          onPrevClick: async () => {
            await triggerClickStart({
              clickType: "previous",
              onClickAction: popover.onPreviousClick,
              notify,
              targetDriver,
              onDestroyed,
              stepTarget: driverSteps[index - 1]?.element,
            });
          },
          onCloseClick: () => {
            if (onDestroyed) onDestroyed();
            targetDriver.destroy();
          },
        },
        onHighlighted: () => {
          if (element) {
            const targetElement = findElement(element);
            if (targetElement && targetElement?.tagName !== "INPUT") {
              targetElement.onclick = () => {
                document.getElementById("popoverNextId")?.click();
              };
            }
          }
        },
      };
    }),
  });
  return targetDriver;
};
